import { createSelector } from 'reselect';
import { RootState } from 'store/types';
import { oc } from 'ts-optchain';
import { consumerDataSelector } from '../consumer/selectors';
import {
  activeConsumerDataSelector,
  providerDetailsDataSelector,
  specialtyDataSelector
} from '../activeConsumer/selectors';
import {
  visitContextDataSelector,
  disclaimersAcknowledgedSelector
} from '../visitContext/selectors';
import {
  hasStartedVisit,
  isTransferringVisit,
  hasDeclinedVisit,
  hasCanceledVisit,
  hasEndedVisit
} from './utils';

export const visitSelector = (state: RootState) => state.amwell.visit;

export const telehealthVideoInstallUrlSelector = createSelector(
  visitSelector,
  v => v.telehealthVideoInstallUrl
);

// Visit Coupon Selectors
export const visitCouponSelector = createSelector(visitSelector, v => v.coupon);

export const visitCouponLoadingSelector = createSelector(visitCouponSelector, vc => vc.loading);
export const visitCouponErrorSelector = createSelector(visitCouponSelector, vc => vc.error);

// Visit Selectors
export const visitDetailsSelector = createSelector(visitSelector, v => v.details);

export const visitDataSelector = createSelector(visitDetailsSelector, vd => vd.data);

export const visitCreateLoadingSelector = createSelector(
  visitDetailsSelector,
  vd => vd.loading.create
);

export const visitCreateErrorSelector = createSelector(
  visitDetailsSelector,
  vd => vd.errors.create
);

export const visitStartLoadingSelector = createSelector(
  visitDetailsSelector,
  vd => vd.loading.start
);

export const visitStartErrorSelector = createSelector(visitDetailsSelector, vd => vd.errors.start);

export const visitTransferLoadingSelector = createSelector(
  visitDetailsSelector,
  vd => vd.loading.transfer
);

export const visitTransferErrorSelector = createSelector(
  visitDetailsSelector,
  vd => vd.errors.transfer
);

export const visitProcessLoadingSelector = createSelector(
  visitDetailsSelector,
  vd => vd.loading.process
);

export const visitProcessErrorSelector = createSelector(
  visitDetailsSelector,
  vd => vd.errors.process
);

export const visitTypeSelector = createSelector(visitDetailsSelector, vd => vd.type);
export const visitStepSelector = createSelector(visitDetailsSelector, vd => vd.step);

// Visit Summary Selectors
export const visitSummarySelector = createSelector(visitSelector, v => v.summary);
export const visitSummaryErrorSelector = createSelector(visitSummarySelector, vs => vs.error);
export const visitSummaryLoadingSelector = createSelector(visitSummarySelector, vs => vs.loading);
export const visitSummaryDataSelector = createSelector(visitSummarySelector, vs => vs.data);

export const visitSummaryDataDataSelector = createSelector(visitSummaryDataSelector, vsd =>
  // eslint-disable-next-line no-underscore-dangle
  oc(vsd).__data({
    endReason: 'fallback', // typically would be PROVIDER_END or PATIENT_END
    disposition: 'fallback', // 'Completed' is the normal disposition after visit
    schedule: { isScheduled: false }
  })
);

export const visitDispositionSelector = createSelector(visitSummaryDataDataSelector, vsdd =>
  oc(vsdd).disposition('fallback')
);

export const visitEndReasonSelector = createSelector(visitSummaryDataDataSelector, vsdd =>
  oc(vsdd).endReason('fallback')
);

export const visitScheduleSelector = createSelector(visitSummaryDataDataSelector, vsdd =>
  oc(vsdd).schedule({ isScheduled: false })
);

export const visitIsScheduledSelector = createSelector(visitScheduleSelector, vss =>
  oc(vss).isScheduled(false)
);

// Waiting Room Alerts Selectors
export const visitWaitingRoomAlertsSelector = createSelector(visitSelector, v => v.alerts);

export const visitWaitingRoomAlertsLoadingSelector = createSelector(
  visitWaitingRoomAlertsSelector,
  v => v.loading
);
export const visitWaitingRoomAlertsErrorSelector = createSelector(
  visitWaitingRoomAlertsSelector,
  v => v.error
);

// Waiting Room Chat Selectors
export const visitWaitingRoomChatSelector = createSelector(visitSelector, v => v.chat);

export const addChatMessageDataSelector = createSelector(visitWaitingRoomChatSelector, v => v.data);
export const addChatMessageErrorSelector = createSelector(
  visitWaitingRoomChatSelector,
  v => v.error
);
export const addChatMessageLoadingSelector = createSelector(
  visitWaitingRoomChatSelector,
  v => v.loading
);

// WebRTC Selectors
export const webRTCVideoConsoleSelector = createSelector(visitSelector, v => v.webRTCVideoConsole);

export const webRTCVideoConsoleDataSelector = createSelector(
  webRTCVideoConsoleSelector,
  w => w.data
);
export const webRTCVideoConsoleErrorSelector = createSelector(
  webRTCVideoConsoleSelector,
  w => w.error
);
export const webRTCVideoConsoleLoadingSelector = createSelector(
  webRTCVideoConsoleSelector,
  w => w.loading
);

// Connect Care Visit Form Routes Requirements Checker Selector
export const visitRequirementsSelector = createSelector(
  [
    consumerDataSelector,
    activeConsumerDataSelector,
    providerDetailsDataSelector,
    specialtyDataSelector,
    disclaimersAcknowledgedSelector,
    visitContextDataSelector,
    visitDataSelector
  ],
  (
    consumer,
    activeConsumer,
    providerDetails,
    specialty,
    disclaimersAcknowledged,
    visitContext,
    visit
  ) => ({
    hasLocation: !!consumer?.legalResidence,
    hasActiveConsumer: !!activeConsumer,
    hasProviderOrSpecialty: !!providerDetails || !!specialty,
    hasVisitContext: !!disclaimersAcknowledged && !!visitContext?.callbackNumber,
    hasActiveVisit: hasStartedVisit(visit) || isTransferringVisit(visit),
    hasCanceledOrEndedVisit:
      hasDeclinedVisit(visit) || hasCanceledVisit(visit) || hasEndedVisit(visit)
  })
);

export const visitTransferProviderSelector = createSelector(
  visitDetailsSelector,
  vd => vd.data?.suggestedProviderForTransfer || vd.data?.providerForTransfer
);
export const vistSuggestedTransferSelector = createSelector(
  visitDetailsSelector,
  vd => vd.data?.suggestedProviderForTransfer !== null
);
